import { reloadFresh } from "@/lib/reloadFresh";
import { reportClientError } from "@/lib/sentry";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw as RefreshIcon, HardDrive, Bug } from "lucide-react";
import { useRouteError } from "react-router-dom";
import { track } from "@/lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// UI Component for Error State
const ErrorUI = ({ error, onReload }: { error: any, onReload: () => void }) => {
  const lowerMsg = (error?.message || "").toLowerCase();
  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  const isChunkError = lowerMsg.includes("failed to fetch dynamically imported module") ||
    lowerMsg.includes("error loading dynamically imported module") ||
    lowerMsg.includes("importing a module script failed") ||
    lowerMsg.includes("loading chunk") ||
    error?.name === "ChunkLoadError";
  // A chunk fetch that fails while offline is a connection problem, not a
  // new-version situation — message it accordingly.
  const isNetworkError = isOffline || (isChunkError && lowerMsg.includes("failed to fetch"));

  const isStorageError = error?.name === "QuotaExceededError" ||
    error?.message?.includes("FILE_ERROR_NO_SPACE") ||
    error?.message?.includes("database connection is closing") ||
    error?.message?.includes("IndexedDB");

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex flex-col items-center justify-center p-4 text-center">
      <div className="max-w-md w-full bg-[var(--bg-surface)] border border-[var(--border-primary)] rounded-[2rem] p-8 shadow-2xl relative overflow-hidden">

        <div className="relative z-10 space-y-6">
          <div className={`w-20 h-20 rounded-3xl mx-auto flex items-center justify-center border bg-[var(--bg-primary)] ${isChunkError ? "border-green-500/30 text-green-500" : isStorageError ? "border-amber-500/30 text-amber-500" : "border-tok-accent-primary/30 text-[var(--accent-primary)]"}`}>
            {isChunkError ? <RefreshIcon className="w-10 h-10" /> : isStorageError ? <HardDrive className="w-10 h-10" /> : <Bug className="w-10 h-10" />}
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-[var(--text-primary)] tracking-tight">
              {isNetworkError ? "No internet connection" : isChunkError ? "Update available" : isStorageError ? "Internal Storage Issue" : "Something went wrong"}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {isNetworkError
                ? "Your connection looks slow or offline. Check your internet and try again. Your progress is safe."
                : isChunkError
                  ? "A new version of Novari Study is available. Please reload to apply the update."
                  : isStorageError
                    ? "Your browser's storage is full or disconnected. Please try clearing your cache or freeing up disk space."
                    : "That's on us, not you. Your progress is safe. Give it another try, and we've already been notified so we can look into it."}
            </p>
          </div>

          <Button
            onClick={onReload}
            className={`w-full h-12 rounded-xl text-xs font-bold uppercase tracking-[0.2em] transition-all
              ${isChunkError
                ? "bg-green-500 hover:bg-green-600 text-[var(--bg-primary)]"
                : isStorageError
                  ? "bg-amber-500 hover:bg-amber-600 text-[var(--bg-primary)]"
                  : "bg-black dark:bg-white text-white dark:text-black shadow-lg hover:opacity-90"}`}
          >
            Reload application
          </Button>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-2 opacity-50">
        <span className="text-[12px] font-bold text-[var(--text-secondary)]">Novari Study</span>
      </div>
    </div>
  );
};

// Original Class Component (for bubbling errors)
export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportClientError(error, "GlobalErrorBoundary");
    console.error("Uncaught error:", error, errorInfo);
    track(ANALYTICS_EVENTS.JS_EXCEPTION, {
      message: error.message,
      name: error.name,
      component_stack: errorInfo.componentStack?.slice(0, 500),
    });
  }

  private handleReload = () => {
    void reloadFresh();
  };

  public render() {
    if (this.state.hasError) {
      return <ErrorUI error={this.state.error} onReload={this.handleReload} />;
    }

    return this.props.children;
  }
}

// Functional Component Hook (for React Router errorElement)
export function GlobalErrorFallback() {
  const error = useRouteError();
  const handleReload = () => { void reloadFresh(); };

  React.useEffect(() => {
    reportClientError(error, "GlobalErrorFallback");
    const err = error as any;
    track(ANALYTICS_EVENTS.ROUTE_ERROR, {
      message: err?.message || err?.statusText || String(err),
      path: window.location.pathname,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ErrorUI error={error} onReload={handleReload} />;
}
